import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getClient, translateError } from "@vixio/data";
import { Button, Callout, Tabs, TextInput } from "@vixio/ui";
import { AuthLayout } from "./AuthLayout";
import { useAuth } from "./AuthProvider";

type Stage = "form" | "sent";

/** `?area=cliente` nas telas de senha mantém a variação do portal. */
function useArea(): "panel" | "portal" {
  const [params] = useSearchParams();
  return params.get("area") === "cliente" ? "portal" : "panel";
}

export function LoginPage({ area = "panel" }: { area?: "panel" | "portal" }) {
  const { session } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const portal = area === "portal";
  const from = (location.state as { from?: string } | null)?.from ?? (portal ? "/portal" : "/");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>("form");

  if (session && stage === "form") {
    return <Navigate to={from} replace />;
  }

  async function withPassword() {
    setError(null);
    if (!email || !password) {
      setError("Informe e-mail e senha.");
      return;
    }
    setBusy(true);
    try {
      const { error: e } = await getClient().auth.signInWithPassword({ email: email.trim(), password });
      if (e) throw e;
      navigate(from, { replace: true });
    } catch (e) {
      setError(translateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  async function withMagicLink() {
    setError(null);
    if (!email) {
      setError("Informe o e-mail.");
      return;
    }
    setBusy(true);
    try {
      const { error: e } = await getClient().auth.signInWithOtp({
        email: email.trim(),
        options: { shouldCreateUser: false, emailRedirectTo: `${window.location.origin}${from}` },
      });
      if (e) throw e;
      setStage("sent");
    } catch (e) {
      setError(translateError(e).message);
    } finally {
      setBusy(false);
    }
  }

  if (stage === "sent") {
    return (
      <AuthLayout title="Confira seu e-mail" area={area}>
        <Callout tone="ok" title="Link enviado">
          Se houver uma conta para <strong>{email}</strong>, o link de acesso chega em instantes. Ele vale por pouco tempo e
          só pode ser usado uma vez.
        </Callout>
        <p style={{ marginTop: 16 }}>
          <Button variant="link" onClick={() => setStage("form")}>
            Voltar
          </Button>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={portal ? "Entrar na área do cliente" : "Entrar"}
      subtitle={portal ? "Use o e-mail em que você recebeu o convite." : "Use o e-mail cadastrado na sua organização."}
      area={area}
    >
      <Tabs
        label="Forma de entrar"
        tabs={[
          {
            value: "senha",
            label: "Senha",
            content: (
              <form
                className="vx-stack"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void withPassword();
                }}
              >
                <TextInput label="E-mail" type="email" autoComplete="username" value={email} onChange={setEmail} required autoFocus />
                <TextInput label="Senha" type="password" autoComplete="current-password" value={password} onChange={setPassword} required />
                {error ? <Callout tone="crit">{error}</Callout> : null}
                <Button type="submit" variant="primary" loading={busy} block>
                  Entrar
                </Button>
                <p>
                  <Link to={portal ? "/recuperar-senha?area=cliente" : "/recuperar-senha"}>Esqueci a senha</Link>
                </p>
              </form>
            ),
          },
          {
            value: "link",
            label: "Link por e-mail",
            content: (
              <form
                className="vx-stack"
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void withMagicLink();
                }}
              >
                <TextInput label="E-mail" type="email" autoComplete="username" value={email} onChange={setEmail} required hint="Enviamos um link de acesso de uso único." />
                {error ? <Callout tone="crit">{error}</Callout> : null}
                <Button type="submit" variant="primary" loading={busy} block>
                  Enviar link de acesso
                </Button>
              </form>
            ),
          },
        ]}
      />
    </AuthLayout>
  );
}

export function RecoverPasswordPage() {
  const area = useArea();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <AuthLayout title="Recuperar senha" subtitle="Enviamos um link para você criar uma senha nova." area={area}>
      {sent ? (
        <Callout tone="ok" title="Link enviado">
          Se houver uma conta para <strong>{email}</strong>, o e-mail chega em instantes.
        </Callout>
      ) : (
        <form
          className="vx-stack"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            setBusy(true);
            try {
              const { error: err } = await getClient().auth.resetPasswordForEmail(email.trim(), {
                redirectTo: `${window.location.origin}/redefinir-senha${area === "portal" ? "?area=cliente" : ""}`,
              });
              if (err) throw err;
              setSent(true);
            } catch (err) {
              setError(translateError(err).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextInput label="E-mail" type="email" autoComplete="username" value={email} onChange={setEmail} required autoFocus />
          {error ? <Callout tone="crit">{error}</Callout> : null}
          <Button type="submit" variant="primary" loading={busy} block>
            Enviar link
          </Button>
        </form>
      )}
      <p style={{ marginTop: 16 }}>
        <Link to={area === "portal" ? "/portal/entrar" : "/entrar"}>Voltar para a entrada</Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const area = useArea();
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (loading) return null;
  if (!session) {
    return (
      <AuthLayout title="Link inválido" area={area}>
        <Callout tone="warn">O link de redefinição venceu ou já foi usado. Peça outro.</Callout>
        <p style={{ marginTop: 16 }}>
          <Link to={area === "portal" ? "/recuperar-senha?area=cliente" : "/recuperar-senha"}>Pedir novo link</Link>
        </p>
      </AuthLayout>
    );
  }
  const mismatch = pw2.length > 0 && pw !== pw2;
  return (
    <AuthLayout title="Nova senha" subtitle={`Conta ${session.user.email ?? ""}`} area={area}>
      {done ? (
        <Callout tone="ok" title="Senha alterada">
          Use a nova senha no próximo acesso.
        </Callout>
      ) : (
        <form
          className="vx-stack"
          noValidate
          onSubmit={async (e) => {
            e.preventDefault();
            setError(null);
            if (pw.length < 10) {
              setError("Use pelo menos 10 caracteres.");
              return;
            }
            if (pw !== pw2) return;
            setBusy(true);
            try {
              const { error: err } = await getClient().auth.updateUser({ password: pw, data: { password_set: true } });
              if (err) throw err;
              setDone(true);
              setTimeout(() => navigate(area === "portal" ? "/portal" : "/", { replace: true }), 1200);
            } catch (err) {
              setError(translateError(err).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          <TextInput label="Nova senha" type="password" autoComplete="new-password" value={pw} onChange={setPw} required hint="Mínimo de 10 caracteres." />
          <TextInput
            label="Repita a nova senha"
            type="password"
            autoComplete="new-password"
            value={pw2}
            onChange={setPw2}
            required
            error={mismatch ? "As senhas não conferem." : null}
          />
          {error ? <Callout tone="crit">{error}</Callout> : null}
          <Button type="submit" variant="primary" loading={busy} block>
            Salvar senha
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

/** Primeiro acesso de quem entrou pelo link do convite: cria a senha para os próximos acessos. */
export function FirstAccessPage() {
  const { session, loading } = useAuth();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (loading) return null;
  if (!session) return <Navigate to="/portal/entrar" replace />;
  const mismatch = pw2.length > 0 && pw !== pw2;
  return (
    <AuthLayout title="Crie sua senha" subtitle={`Bem-vindo. Esta será a senha da conta ${session.user.email ?? ""}.`} area="portal">
      <form
        className="vx-stack"
        noValidate
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          if (pw.length < 10) {
            setError("Use pelo menos 10 caracteres.");
            return;
          }
          if (pw !== pw2) return;
          setBusy(true);
          try {
            const { error: err } = await getClient().auth.updateUser({ password: pw, data: { password_set: true } });
            if (err) throw err;
            navigate("/portal", { replace: true });
          } catch (err) {
            setError(translateError(err).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <TextInput label="Senha" type="password" autoComplete="new-password" value={pw} onChange={setPw} required hint="Mínimo de 10 caracteres. Uma frase curta é fácil de lembrar e difícil de adivinhar." />
        <TextInput
          label="Repita a senha"
          type="password"
          autoComplete="new-password"
          value={pw2}
          onChange={setPw2}
          required
          error={mismatch ? "As senhas não conferem." : null}
        />
        {error ? <Callout tone="crit">{error}</Callout> : null}
        <Button type="submit" variant="primary" loading={busy} block>
          Salvar e entrar
        </Button>
        <p className="text-2" style={{ fontSize: "var(--fs-13)" }}>
          Prefere entrar sempre por link no e-mail? <Link to="/portal">Agora não</Link>
        </p>
      </form>
    </AuthLayout>
  );
}
